import React, { RefObject, useMemo } from 'react';
import { withDataConverter, withErrorBoundary } from '@beef/react';
import { TabBar } from '@carnica/ui-kit';
import { compose, getStore } from '@beef/redux';
import type { MenuDefaultItem, TabBarDefaultItem } from '@carnica/ui-kit/types';
import { Provider } from 'react-redux';

import { TabBarWrapper } from 'pages-components/TabBarWrapper';
import { ISectionParsed } from 'pages/MainPage/types';
import { commonMetric } from 'pages/MainPage/metric/common';
import { handleItemClick } from 'pages-components/TabBarWrapper/utils';

import { convertTabBarBlock } from './utils';
import { useNearestSection } from './hooks/useNearestPosition';
import { useCalculateTransformStyle } from './hooks/useCalculateTransformStyle';
import { useCalculateTransitionStyle } from './hooks/useCalculateTransitionStyle';
import { connector } from './connector';

export interface TabBarBlockProps {
  anchorsIds: string[];
  isMobile: boolean;
  items: TabBarDefaultItem[];
  menu: MenuDefaultItem[];
  sectionRef: RefObject<HTMLDivElement>;
  sectionsParsed: ISectionParsed;
}

export const _TabBarBlock: React.FC<TabBarBlockProps> = ({
  sectionRef,
  menu,
  items,
  anchorsIds,
  isMobile,
  sectionsParsed,
}) => {
  const activeSection = useNearestSection(anchorsIds, sectionsParsed);
  const activeMenuItem = useMemo(() => {
    if (!activeSection) return items[0];
    return items.find((item) => item.key === activeSection);
  }, [activeSection]);

  const transformStyle = useCalculateTransformStyle(sectionRef);
  const transition = useCalculateTransitionStyle();
  const store = getStore('headerStore');

  return (
    <Provider store={store}>
      <TabBarWrapper
        style={{
          transform: isMobile ? 'unset' : transformStyle,
          transition,
        }}
      >
        <TabBar
          closeMenuOnScroll
          getMenuAttributes={(item: MenuDefaultItem) => ({
            href: item.href,
            target: item.target,
          })}
          getMenuTagName={(item: MenuDefaultItem) => (item.href ? 'a' : 'button')}
          items={items}
          menu={menu}
          menuAttributes={{
            style: { zIndex: 25 },
          }}
          onItemClick={(item: TabBarDefaultItem) => {
            commonMetric.handleTabBarClick(item.label);
            handleItemClick(item.key);
          }}
          onMenuButtonClick={() => commonMetric.handleTabBarButtonClick()}
          onMenuClick={(item: TabBarDefaultItem) => commonMetric.handleTabBarMenuClick(item.label)}
          position={isMobile ? 'fixed' : 'default'}
          value={activeMenuItem}
        />
      </TabBarWrapper>
    </Provider>
  );
};

export const TabBarBlock = compose<React.FC<TabBarBlockProps>>(
  withErrorBoundary({ fallback: null }),
  connector,
  withDataConverter(convertTabBarBlock),
)(_TabBarBlock);
